import React from 'react';
import { TuxView, ValueWithLabel } from '@amzn/tux-static-website';
import AdminRequestHelper, {
    DailyErrorsSummary,
    DailyPageVisitsSummary,
    DailyVisitsSummary,
    MonthlyUsersResponse,
    MonthlyVisitsResponse
} from '../../../utils/admin_request_helper';
import { Button, ColumnLayout, Container, ExpandableSection, FormField, Grid, Header, Select, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import DelayedLoadExpandable from '../../../components/delayed_load_expandable';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

interface AnalyticsViewProps {}

export default class AnalyticsView extends TuxView<AnalyticsViewProps, any> {
    private isLoading = true;
    monthlyUsersSummary: MonthlyUsersResponse[] = [];
    dailyVisitsSummary: DailyVisitsSummary[] = [];
    dailyPageVisitsSummary: DailyPageVisitsSummary[] = [];
    dailyErrorsSummary: DailyErrorsSummary[] = [];
    private monthlyTrafficMonth = new Date().getMonth() + 1;
    private monthlyTrafficYear = new Date().getFullYear();
    private monthlyTrafficData: DailyPageVisitsSummary[] | null = null;
    private monthlyTrafficLoading = false;

    constructor(props: AnalyticsViewProps) {
        super(props);
        this.bindAll(this);
        this.loadData();
    }

    async loadData() {
        this.isLoading = false;
        this.dataUpdated();
    }

    private async loadDailyPageVisits(): Promise<{ pages: DailyPageVisitsSummary[]; visits: DailyVisitsSummary[] }> {
        let visits = await AdminRequestHelper.analyticsRequest<DailyVisitsSummary[]>('analytics/dailyVisits', {
            method: 'get'
        });
        let pages = await AdminRequestHelper.analyticsRequest<DailyPageVisitsSummary[]>('analytics/pageVisits', {
            method: 'get'
        });

        if (Array.isArray(visits)) {
            visits.sort(
                (a, b) =>
                    new Date(`${a._id.year}-${a._id.month}-${a._id.day}`).getTime() -
                    new Date(`${b._id.year}-${b._id.month}-${b._id.day}`).getTime()
            );
        } else {
            visits = [];
        }

        if (Array.isArray(pages)) {
            pages.sort(
                (a, b) =>
                    new Date(`${a._id.year}-${a._id.month}-${a._id.day}`).getTime() -
                    new Date(`${b._id.year}-${b._id.month}-${b._id.day}`).getTime()
            );
        } else {
            pages = [];
        }

        return { pages, visits };
    }

    private async loadMonthlyUsers(): Promise<MonthlyUsersResponse[]> {
        let results: MonthlyUsersResponse[] = await AdminRequestHelper.analyticsRequest<MonthlyUsersResponse[]>(
            'analytics/monthlyUsers',
            { method: 'get' }
        );

        if (Array.isArray(results)) {
            results.sort((a, b) => `${a._id.year}${String(a._id.month).padStart(2, '0')}`.localeCompare(`${b._id.year}${String(b._id.month).padStart(2, '0')}`));
        } else {
            results = [];
        }

        return results;
    }

    private async loadMonthlyVisits(): Promise<MonthlyVisitsResponse[]> {
        let results: MonthlyVisitsResponse[] = await AdminRequestHelper.analyticsRequest<MonthlyVisitsResponse[]>(
            'analytics/monthlyVisits',
            { method: 'get' }
        );

        if (Array.isArray(results)) {
            results.sort((a, b) => `${a._id.year}${String(a._id.month).padStart(2, '0')}`.localeCompare(`${b._id.year}${String(b._id.month).padStart(2, '0')}`));
        } else {
            results = [];
        }

        return results;
    }

    private async loadErrorSummary(): Promise<DailyErrorsSummary[]> {
        const results = await AdminRequestHelper.analyticsRequest<DailyErrorsSummary[]>('analytics/errorsPerDay', {
            method: 'get'
        });

        if (!Array.isArray(results)) {
            return [];
        }

        return DailyErrorsSummary.fromJSON(results);
    }

    private getMonth(month: number): string {
        return MONTHS[month - 1] ?? `${month}`;
    }

    private async loadMonthlyPageTraffic() {
        this.monthlyTrafficLoading = true;
        this.monthlyTrafficData = null;
        this.dataUpdated();

        let results = await AdminRequestHelper.analyticsRequest<DailyPageVisitsSummary[]>(
            `analytics/monthlyPageTraffic?month=${this.monthlyTrafficMonth}&year=${this.monthlyTrafficYear}`,
            { method: 'get' }
        );
        this.monthlyTrafficData = Array.isArray(results)
            ? results.sort((a, b) => a._id.day - b._id.day || a._id.page.localeCompare(b._id.page))
            : [];
        this.monthlyTrafficLoading = false;
        this.dataUpdated();
    }

    private getDaysInMonth(month: number, year: number): number {
        return new Date(year, month, 0).getDate();
    }

    private getMonthlyPageTrafficSection() {
        const data = this.monthlyTrafficData;
        const grouped = new Map<number, DailyPageVisitsSummary[]>();
        if (data) {
            for (const entry of data) {
                if (!grouped.has(entry._id.day)) grouped.set(entry._id.day, []);
                grouped.get(entry._id.day)!.push(entry);
            }
        }
        const totalVisits = data ? data.reduce((sum, d) => sum + d.count, 0) : 0;
        const daysInMonth = this.getDaysInMonth(this.monthlyTrafficMonth, this.monthlyTrafficYear);

        return (
            <Container
                header={
                    <Header
                        variant="h2"
                        description="Per-page visit counts grouped by day for a selected month. Counts every visit, not unique users."
                    >
                        Monthly Page Traffic
                    </Header>
                }
            >
                <SpaceBetween direction="vertical" size="m">
                    <SpaceBetween direction="horizontal" size="m">
                        <FormField label="Month">
                            <Select
                                selectedOption={{ label: this.getMonth(this.monthlyTrafficMonth), value: `${this.monthlyTrafficMonth}` }}
                                options={MONTHS.map((m, i) => ({ label: m, value: `${i + 1}` }))}
                                onChange={({ detail }) => {
                                    this.monthlyTrafficMonth = +detail.selectedOption.value!;
                                    this.dataUpdated();
                                }}
                            />
                        </FormField>
                        <FormField label="Year">
                            <Select
                                selectedOption={{ label: `${this.monthlyTrafficYear}`, value: `${this.monthlyTrafficYear}` }}
                                options={Array.from({ length: 5 }, (_, i) => {
                                    const y = new Date().getFullYear() - i;
                                    return { label: `${y}`, value: `${y}` };
                                })}
                                onChange={({ detail }) => {
                                    this.monthlyTrafficYear = +detail.selectedOption.value!;
                                    this.dataUpdated();
                                }}
                            />
                        </FormField>
                        <FormField label="&nbsp;">
                            <Button variant="primary" onClick={() => this.loadMonthlyPageTraffic()} loading={this.monthlyTrafficLoading}>
                                Load
                            </Button>
                        </FormField>
                    </SpaceBetween>

                    {this.monthlyTrafficLoading && <Spinner size="big" />}

                    {data && !this.monthlyTrafficLoading && (
                        <>
                            <p>{totalVisits} total page visits in {this.getMonth(this.monthlyTrafficMonth)} {this.monthlyTrafficYear}</p>
                            <ColumnLayout columns={3}>
                                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                                    const pages = grouped.get(day) ?? [];
                                    const dayTotal = pages.reduce((s, p) => s + p.count, 0);
                                    return (
                                        <ExpandableSection
                                            key={day}
                                            header={`${day} ${this.getMonth(this.monthlyTrafficMonth)} ${this.monthlyTrafficYear} - ${dayTotal} Visits`}
                                        >
                                            <pre style={{ maxHeight: '150px', overflowY: 'auto', minWidth: '150px' }}>
                                                {pages.length > 0
                                                    ? pages.map(p => `${p._id.page} had ${p.count} visits`).join('\n')
                                                    : 'No visits'}
                                            </pre>
                                        </ExpandableSection>
                                    );
                                })}
                            </ColumnLayout>
                        </>
                    )}
                </SpaceBetween>
            </Container>
        );
    }

    private getAllUsers(summary: MonthlyUsersResponse) {
        return (
            <ExpandableSection header={`${summary.users.length} unique users`}>
                <pre style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {summary.users.filter(u => u !== null && u.length > 0).map(u => `${u}\n`)}
                </pre>
            </ExpandableSection>
        );
    }

    private getNewUsers(summary: MonthlyUsersResponse, lastSummary: MonthlyUsersResponse) {
        const newUsers = summary.users.filter(u => !lastSummary.users.includes(u));

        return (
            <ExpandableSection header={`${newUsers.length} new users (vs last month)`}>
                <pre style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {newUsers.filter(u => u !== null && u.length > 0).map(u => `${u}\n`)}
                </pre>
            </ExpandableSection>
        );
    }

    private getSummary(summary: MonthlyUsersResponse, lastSummary?: MonthlyUsersResponse) {
        return (
            <ValueWithLabel
                key={`${summary._id.month}/${summary._id.year}`}
                label={`${this.getMonth(summary._id.month)} ${summary._id.year}`}
            >
                <Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }]}>
                    {this.getAllUsers(summary)}
                    {lastSummary ? this.getNewUsers(summary, lastSummary) : null}
                </Grid>
            </ValueWithLabel>
        );
    }

    private getMonthlySummary(monthlyUsersSummary: MonthlyUsersResponse[]) {
        return (
            <>
                {monthlyUsersSummary.length > 0 ? (
                    <SpaceBetween size="s">
                        {monthlyUsersSummary.map((summary, index) =>
                            this.getSummary(summary, monthlyUsersSummary[index - 1])
                        )}
                    </SpaceBetween>
                ) : (
                    'No Data Present'
                )}
            </>
        );
    }

    private getMonthlyVisitsSummary(monthlyVisits: MonthlyVisitsResponse[]) {
        return (
            <>
                {monthlyVisits.length > 0 ? (
                    <SpaceBetween size="s">
                        {monthlyVisits.map((entry) => (
                            <ValueWithLabel
                                key={`${entry._id.month}/${entry._id.year}`}
                                label={`${this.getMonth(entry._id.month)} ${entry._id.year}`}
                            >
                                {entry.totalVisits} total sessions
                            </ValueWithLabel>
                        ))}
                    </SpaceBetween>
                ) : (
                    'No Data Present'
                )}
            </>
        );
    }

    private getDailySummary(summary: DailyVisitsSummary, pages: DailyPageVisitsSummary[]) {
        return (
            <ExpandableSection
                key={`${summary._id.day}${summary._id.month}${summary._id.year}`}
                header={`${summary._id.day} ${this.getMonth(summary._id.month)} ${summary._id.year} - ${
                    summary.visits
                } Visits`}
            >
                <pre style={{ maxHeight: '150px', overflowY: 'auto', minWidth: '150px' }}>
                    {pages.length > 0
                        ? pages
                              ?.filter(
                                  s =>
                                      s._id.day === summary._id.day &&
                                      s._id.month === summary._id.month &&
                                      s._id.year === summary._id.year
                              )
                              ?.map(s => `${s._id.page} had ${s.count} visits`)
                              ?.join('\n')
                        : 'Failed to fetch breakdown'}
                </pre>
            </ExpandableSection>
        );
    }

    private getDailyVisitsSummary(data: { pages: DailyPageVisitsSummary[]; visits: DailyVisitsSummary[] }) {
        return (
            <>
                {data.visits.length > 0 ? (
                    <ColumnLayout columns={3}>
                        {data.visits.map(summary => this.getDailySummary(summary, data.pages))}
                    </ColumnLayout>
                ) : (
                    'No Data Present'
                )}
            </>
        );
    }

    private getErrorSummary(summary: DailyErrorsSummary) {
        let i = 0;
        return (
            <ExpandableSection
                key={`${summary._id.day}${summary._id.month}${summary._id.year}`}
                header={`${summary._id.day} ${this.getMonth(summary._id.month)} ${summary._id.year} - ${
                    summary.errors.length
                } Errors`}
            >
                {summary.errors.map(error => (
                    <ExpandableSection key={`ERROR_${i++}`} header={error.error}>
                        <pre style={{ maxHeight: '150px', overflowY: 'auto', minWidth: '150px' }}>
                            {JSON.stringify(error, null, 4)}
                        </pre>
                    </ExpandableSection>
                ))}
            </ExpandableSection>
        );
    }

    private getDailyErrorsSummary(errorSummaries: DailyErrorsSummary[]) {
        return (
            <>
                {errorSummaries.length > 0 ? (
                    <ColumnLayout columns={3}>
                        {errorSummaries.map(summary => this.getErrorSummary(summary))}
                    </ColumnLayout>
                ) : (
                    'No Data Present'
                )}
            </>
        );
    }

    render() {
        if (this.isLoading) {
            return <Spinner size="big" />;
        }

        return (
            <SpaceBetween direction="vertical" size="m">
                <DelayedLoadExpandable<MonthlyUsersResponse[]>
                    variant="container"
                    title={'Monthly Unique Users (Past 12 Months)'}
                    description={'Counts distinct users per month. Multiple sessions from the same user count as 1.'}
                    loadData={this.loadMonthlyUsers}
                    content={this.getMonthlySummary}
                />
                <DelayedLoadExpandable<MonthlyVisitsResponse[]>
                    variant="container"
                    title={'Monthly Total Visits (Past 12 Months)'}
                    description={'Counts every page visit. Multiple sessions from the same user each count separately.'}
                    loadData={this.loadMonthlyVisits}
                    content={this.getMonthlyVisitsSummary}
                />
                <DelayedLoadExpandable<{ pages: DailyPageVisitsSummary[]; visits: DailyVisitsSummary[] }>
                    variant="container"
                    title={'Daily Page Traffic (Past 14 Days)'}
                    description={'Per-page visit counts grouped by day. Counts every visit, not unique users.'}
                    loadData={this.loadDailyPageVisits}
                    content={this.getDailyVisitsSummary}
                />
                {this.getMonthlyPageTrafficSection()}
                <DelayedLoadExpandable<DailyErrorsSummary[]>
                    variant="container"
                    title={'Daily Error Summaries (Past 14 Days)'}
                    loadData={this.loadErrorSummary}
                    content={this.getDailyErrorsSummary}
                />
            </SpaceBetween>
        );
    }
}
